import { useState, useEffect } from 'react';
import api from '../lib/api.js';
import { formatEuro, formatDateISO } from '../lib/format.js';
import { computeQuoteTotals } from '@shared/quote-calculator.js';
import StatusBadge from './ui/StatusBadge.jsx';
import ClientSelector from './ui/ClientSelector.jsx';

export default function QuoteComposer({ quoteId, onSaved, onCancel }) {
  const isEdit = !!quoteId;
  const [form, setForm] = useState({
    client_id: '', client_name: '', title: '',
    issue_date: new Date().toISOString().split('T')[0],
    validity_date: new Date(Date.now() + 30*86400000).toISOString().split('T')[0],
    intro_text: '', terms_text: 'Il presente preventivo ha validità di 30 giorni dalla data di emissione.',
    payment_terms: 'Bonifico bancario a 30 gg data accettazione',
    payment_method: 'Bonifico bancario',
    discount_mode: 'none', discount_value: 0,
    inps_enabled: true, inps_rate: 4,
    stamp_duty_mode: 'auto',
    notes: '', internal_notes: '',
    items: [{ service_id: '', quantity: 1, unit_price: 0, discount_percent: 0 }]
  });
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [totals, setTotals] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [newClientSaving, setNewClientSaving] = useState(false);
  const [newClientError, setNewClientError] = useState('');
  const blankNewClient = {
    new_client_legal_name: '', new_client_first_name: '', new_client_last_name: '',
    new_client_vat_number: '', new_client_tax_code: '', new_client_email: '',
    new_client_phone: '', new_client_street: '', new_client_city: '',
    new_client_province: '', new_client_zip: '',
  };
  const [newClientForm, setNewClientForm] = useState(blankNewClient);

  useEffect(() => {
    api.get('/clients?pageSize=50').then(r => { if (r.ok) setClients(r.data?.data || []); });
    api.get('/services?isActive=true&pageSize=100').then(r => { if (r.ok) setServices(r.data?.data || []); });
    if (quoteId) {
      api.get('/quotes/' + quoteId).then(r => {
        if (r.ok) {
          const d = r.data;
          setForm({
            client_id: String(d.client_id || ''),
            client_name: d.display_name || d.legal_name || '',
            title: d.title || '',
            issue_date: d.issue_date || new Date().toISOString().split('T')[0],
            validity_date: d.validity_date || new Date().toISOString().split('T')[0],
            intro_text: d.intro_text || '',
            terms_text: d.terms_text || '',
            payment_terms: d.payment_terms || '',
            payment_method: d.payment_method || '',
            discount_mode: d.discount_mode || 'none',
            discount_value: d.discount_value || 0,
            inps_enabled: !!d.inps_enabled,
            inps_rate: d.inps_rate || 4,
            stamp_duty_mode: d.stamp_duty_mode || 'auto',
            notes: d.notes || '',
            internal_notes: d.internal_notes || '',
            items: d.items && d.items.length > 0 ? d.items.map(i => ({
              service_id: String(i.service_id || ''),
              quantity: i.quantity || 1,
              unit_price: i.unit_price || 0,
              discount_percent: i.discount_percent || 0
            })) : [{ service_id: '', quantity: 1, unit_price: 0, discount_percent: 0 }]
          });
        }
      });
    }
  }, [quoteId]);

  useEffect(() => {
    const calcInputs = form.items.map(i => ({
      quantity: Number(i.quantity) || 0,
      unit_price: Number(i.unit_price) || 0,
      discount_percent: Number(i.discount_percent) || 0
    }));
    const t = computeQuoteTotals({
      items: calcInputs,
      discount_mode: form.discount_mode,
      discount_value: Number(form.discount_value) || 0,
      inps_enabled: form.inps_enabled,
      inps_rate: Number(form.inps_rate) || 4,
      stamp_duty_mode: form.stamp_duty_mode
    });
    setTotals(t);
  }, [form.items, form.discount_mode, form.discount_value, form.inps_enabled, form.inps_rate, form.stamp_duty_mode]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setItem = (idx, field, value) => {
    const items = form.items.map(i => ({ ...i }));
    items[idx] = { ...items[idx], [field]: value };
    setForm(prev => ({ ...prev, items }));
  };
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { service_id: '', quantity: 1, unit_price: 0, discount_percent: 0 }] }));
  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.client_id) { setError('Seleziona un cliente'); return; }
    if (!form.title?.trim()) { setError('Inserisci un titolo'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        client_id: Number(form.client_id),
        title: form.title,
        issue_date: form.issue_date,
        validity_date: form.validity_date,
        intro_text: form.intro_text,
        terms_text: form.terms_text,
        payment_terms: form.payment_terms,
        payment_method: form.payment_method,
        discount_mode: form.discount_mode,
        discount_value: Number(form.discount_value) || 0,
        inps_enabled: form.inps_enabled,
        inps_rate: Number(form.inps_rate) || 4,
        stamp_duty_mode: form.stamp_duty_mode,
        notes: form.notes,
        internal_notes: form.internal_notes,
        items: form.items.filter(i => i.service_id).map(i => ({
          service_id: Number(i.service_id),
          quantity: Number(i.quantity) || 1,
          unit_price: Number(i.unit_price) || 0,
          discount_percent: Number(i.discount_percent) || 0
        }))
      };
      const res = quoteId
        ? await api.patch('/quotes/' + quoteId, payload)
        : await api.post('/quotes', payload);
      if (res.ok) onSaved?.(res.data);
      else setError(res.error || 'Errore salvataggio');
    } catch (err) { setError(err?.message || 'Errore di rete'); }
    finally { setSaving(false); }
  }

  const selectedClient = clients.find(c => String(c.id) === String(form.client_id));

  function resetNewClientForm() {
    setNewClientForm(blankNewClient);
    setNewClientError('');
  }

  async function handleCreateNewClient(e) {
    e.preventDefault();
    setNewClientError('');
    setNewClientSaving(true);
    try {
      const payload = {
        client_type: 'individual',
        legal_name: newClientForm.new_client_legal_name?.trim() || 'Cliente generico',
        first_name: newClientForm.new_client_first_name || '',
        last_name: newClientForm.new_client_last_name || '',
        vat_number: newClientForm.new_client_vat_number || '',
        tax_code: newClientForm.new_client_tax_code || '',
        email: newClientForm.new_client_email || '',
        phone: newClientForm.new_client_phone || '',
        street: newClientForm.new_client_street || '',
        city: newClientForm.new_client_city || '',
        province: newClientForm.new_client_province || '',
        zip: newClientForm.new_client_zip || '',
        country: 'Italia',
        status: 'active',
      };
      const res = await api.post('/clients', payload);
      if (!res.ok) throw new Error(res.error || 'Creazione fallita');
      setClients(prev => [...prev, res.data]);
      setForm({ ...form, client_id: String(res.data.id), client_name: res.data.display_name || res.data.legal_name });
      setShowNewClient(false);
      resetNewClientForm();
    } catch (err) {
      setNewClientError(err?.message || 'Errore di rete');
    } finally {
      setNewClientSaving(false);
    }
  }

  const filteredClients = clientSearch
    ? clients.filter(c => {
        const q = clientSearch.toLowerCase();
        return (c.display_name || c.legal_name || '').toLowerCase().includes(q)
          || (c.email || '').toLowerCase().includes(q)
          || (c.vat_number || '').toLowerCase().includes(q)
          || (c.tax_code || '').toLowerCase().includes(q);
      })
    : clients;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-surface">{quoteId ? 'Modifica Preventivo' : 'Nuovo Preventivo'}</h2>
        <div className="flex gap-2">
          <button className="btn btn-secondary text-sm" onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? 'Nascondi anteprima' : 'Anteprima'}
          </button>
          <button className="btn btn-ghost" onClick={onCancel}>Chiudi</button>
        </div>
      </div>

      {error && <div className="p-3 rounded-md text-sm" style={{background:'rgba(239,68,68,.1)',color:'#ef4444'}}>{error}</div>}

      {/* Cliente e date */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 card-elevated p-4">
        <Field label="Cliente *" required>
          <ClientSelector
            value={form.client_id}
            clients={clients}
            onChange={(id, name) => setForm({ ...form, client_id: id, client_name: name })}
            onClientsUpdate={setClients}
          />
        </Field>
        <Field label="Data emissione"><input className="input" type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} /></Field>
        <Field label="Scadenza"><input className="input" type="date" value={form.validity_date} onChange={e => set('validity_date', e.target.value)} /></Field>
        <Field label="Stato"><StatusBadge status="draft" small /></Field>
      </div>

      {/* Righe */}
      <div className="card-elevated">
        <div className="flex items-center justify-between mb-3">
          <h3 className="field-label">Righe preventivo</h3>
          <button className="btn btn-primary text-xs" onClick={addItem}>+ Aggiungi riga</button>
        </div>
        <div className="overflow-x-auto">
          <table className="table-root">
            <thead>
              <tr>
                <th className="table-header w-[40%]">Servizio</th>
                <th className="table-header w-[12%]">Qtà</th>
                <th className="table-header w-[15%]">Prezzo</th>
                <th className="table-header w-[12%]">Sconto %</th>
                <th className="table-header w-[15%]">Totale riga</th>
                <th className="table-header w-[6%]"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((item, idx) => (
                <tr key={idx}>
                  <td className="table-cell">
                    <select className="input" value={item.service_id} onChange={e => setItem(idx, 'service_id', e.target.value)} required>
                      <option value="">Seleziona servizio...</option>
                      {services.map(s => <option key={s.id} value={s.id}>{s.code} - {s.name} ({formatEuro(s.unit_price)})</option>)}
                    </select>
                  </td>
                  <td className="table-cell"><input className="input w-full" type="number" min="0.5" step="0.5" value={item.quantity} onChange={e => setItem(idx, 'quantity', e.target.value)} /></td>
                  <td className="table-cell"><input className="input w-full" type="number" step="0.01" min="0" value={item.unit_price} onChange={e => setItem(idx, 'unit_price', e.target.value)} /></td>
                  <td className="table-cell"><input className="input w-full" type="number" min="0" max="100" step="0.1" value={item.discount_percent} onChange={e => setItem(idx, 'discount_percent', e.target.value)} /></td>
                  <td className="table-cell text-right font-mono">
                    {item.quantity && item.unit_price ? formatEuro(Number(item.quantity) * Number(item.unit_price) * (1 - Number(item.discount_percent)/100)) : '\u2014'}
                  </td>
                  <td className="table-cell text-center">
                    <button className="btn btn-ghost text-xs text-danger" onClick={() => removeItem(idx)} disabled={form.items.length <= 1}>X</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totali + Opzioni */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Totali */}
        <div className="card-elevated">
          <h3 className="field-label mb-3">Riepilogo importi</h3>
          <div className="space-y-2 text-sm">
            {totals.subtotal > 0 && (
              <>
                <TotalsRow label="Imponibile lordo" value={totals.subtotal} />
                {totals.discount_amount > 0 && <TotalsRow label={`Sconto (${form.discount_mode==='percent'?form.discount_value+'%':formatEuro(form.discount_value)})`} value={totals.discount_amount} negative />}
                <TotalsRow label="Imponibile netto" value={totals.taxable_amount} strong />
                {totals.inps_amount > 0 && <TotalsRow label={`INPS (${form.inps_rate}%)`} value={totals.inps_amount} />}
                {totals.stamp_duty_amount > 0 && <TotalsRow label="Bollo" value={totals.stamp_duty_amount} />}
                <hr style={{borderColor:'var(--color-line)'}} />
                <TotalsRow label="TOTALE NETTO" value={totals.total_amount} strong big />
              </>
            )}
            {totals.subtotal === 0 && <div className="text-surface-4 text-sm text-center py-4">Aggiungi righe per vedere i totali</div>}
          </div>
        </div>

        {/* Opzioni */}
        <div className="card-elevated space-y-4">
          <h3 className="field-label">Opzioni</h3>
          <Field label="Titolo *" required>
            <input className="input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="es. Realizzazione sito web" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sconto globale">
              <select className="input" value={form.discount_mode} onChange={e => set('discount_mode', e.target.value)}>
                <option value="none">Nessuno</option>
                <option value="percent">Percentuale</option>
                <option value="amount">Importo fisso</option>
              </select>
            </Field>
            {form.discount_mode !== 'none' && (
              <Field label={form.discount_mode === 'percent' ? 'Percentuale %' : 'Importo €'}>
                <input className="input" type="number" step={form.discount_mode==='percent'?'0.1':'0.01'} min="0" value={form.discount_value} onChange={e => set('discount_value', e.target.value)} />
              </Field>
            )}
          </div>
          <Field label="Rivalsa INPS" className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.inps_enabled} onChange={e => set('inps_enabled', e.target.checked)} />
              <span>Attiva</span>
            </label>
            {form.inps_enabled && (
              <Field label="Aliquota %" className="w-24">
                <input className="input" type="number" step="0.1" min="0" max="100" value={form.inps_rate} onChange={e => set('inps_rate', e.target.value)} />
              </Field>
            )}
          </Field>
          <Field label="Imposta di bollo">
            <select className="input w-1/2" value={form.stamp_duty_mode} onChange={e => set('stamp_duty_mode', e.target.value)}>
              <option value="auto">Auto (se &gt; 77,47€)</option>
              <option value="always">Sempre</option>
              <option value="never">Mai</option>
            </select>
          </Field>
          <Field label="Testo introduttivo">
            <textarea className="input min-h-[60px]" value={form.intro_text} onChange={e => set('intro_text', e.target.value)} placeholder="Testo prima delle righe..." />
          </Field>
          <Field label="Condizioni">
            <textarea className="input min-h-[60px]" value={form.terms_text} onChange={e => set('terms_text', e.target.value)} />
          </Field>
          <Field label="Pagamento">
            <input className="input" value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} />
          </Field>
          <Field label="Note interne">
            <textarea className="input min-h-[60px]" value={form.internal_notes} onChange={e => set('internal_notes', e.target.value)} placeholder="Note per uso interno..." />
          </Field>
        </div>
      </div>

      {/* Azioni */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button className="btn btn-primary" onClick={() => setShowPreview(true)}>Mostra anteprima</button>
        <button className="btn btn-primary" disabled={saving} onClick={handleSubmit}>
          {saving ? 'Salvataggio...' : 'Salva preventivo'}
        </button>
      </div>

      {/* Anteprima */}
      {showPreview && <QuotePreview quote={{...form, totals, client: selectedClient}} onClose={() => setShowPreview(false)} />}
    </div>
  );
}

// Sub-components
function Field({ label, required, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${className}`}>
      <span className="text-surface-4 text-xs">{label}{required && ' *'}</span>
      {children}
    </label>
  );
}

function TotalsRow({ label, value, negative, strong, big }) {
  return (
    <div className={`flex justify-between ${negative ? "text-danger" : ""} ${strong ? "font-semibold" : ""} ${big ? "text-base" : ""}`}>
      <span className="text-surface-3">{label}</span>
      <span className="font-mono text-surface">{value !== undefined ? value.toFixed(2).replace(".", ",") + " €" : "\u2014"}</span>
    </div>
  );
}

function QuotePreview({ quote, onClose }) {
  const { totals, items = [], client } = quote;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col" style={{boxShadow:'0 25px 50px -12px rgba(0,0,0,.5)'}}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Anteprima Preventivo</h2>
          <button className="btn btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl mx-auto">
            <div className="mb-6 pb-4 border-b border-gray-200">
              <div className="flex justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">PREVENTIVO</h1>
                  <p className="text-gray-500 mt-1 font-mono">{quote.number || 'PREV-2026-0001'}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">Data: {formatDateISO(quote.issue_date)}</p>
                  <p className="text-sm text-gray-500">Scadenza: {formatDateISO(quote.validity_date)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-xs uppercase text-gray-400 mb-1">Emittente</h3>
                  <p className="font-medium">Studio Tecnico Rossi</p>
                  <p className="text-sm text-gray-500">Via Roma 1, Milano</p>
                  <p className="text-sm text-gray-500">P.IVA: IT12345678901</p>
                </div>
                <div>
                  <h3 className="text-xs uppercase text-gray-400 mb-1">Cliente</h3>
                  <p className="font-medium">{client?.display_name || client?.legal_name || quote.client_name || '\u2014'}</p>
                  <p className="text-sm text-gray-500">{client?.city || '\u2014'}</p>
                  <p className="text-sm text-gray-500">P.IVA: {client?.vat_number || '\u2014'}</p>
                </div>
              </div>
            </div>

            {quote.intro_text && <p className="mb-4 text-gray-600 text-sm">{quote.intro_text}</p>}

            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Descrizione</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Qtà</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Prezzo</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Sconto</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Totale</th>
                </tr>
              </thead>
              <tbody>
                {items.filter(i => i.service_id).map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="px-3 py-3">{item.service_name || 'Servizio'}</td>
                    <td className="px-3 py-3 text-right">{item.quantity}</td>
                    <td className="px-3 py-3 text-right">{Number(item.unit_price).toFixed(2).replace('.',',')} €</td>
                    <td className="px-3 py-3 text-right">{item.discount_percent > 0 ? item.discount_percent + '%' : '\u2014'}</td>
                    <td className="px-3 py-3 text-right font-mono">
                      {(Number(item.quantity) * Number(item.unit_price) * (1 - Number(item.discount_percent)/100)).toFixed(2).replace('.',',')} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 flex justify-end">
              <div className="w-64 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Imponibile lordo</span><span className="font-mono font-medium">{totals.subtotal?.toFixed(2)?.replace('.',',')} €</span></div>
                {totals.discount_amount > 0 && <div className="flex justify-between text-red-600"><span>Sconto</span><span className="font-mono">-{totals.discount_amount.toFixed(2).replace('.',',')} €</span></div>}
                <div className="flex justify-between font-semibold"><span>Imponibile netto</span><span className="font-mono">{totals.taxable_amount?.toFixed(2)?.replace('.',',')} €</span></div>
                {totals.inps_amount > 0 && <div className="flex justify-between"><span className="text-gray-500">INPS ({totals.inps_rate}%)</span><span className="font-mono">{totals.inps_amount.toFixed(2).replace('.',',')} €</span></div>}
                {totals.stamp_duty_amount > 0 && <div className="flex justify-between"><span className="text-gray-500">Bollo</span><span className="font-mono">{totals.stamp_duty_amount.toFixed(2).replace('.',',')} €</span></div>}
                <hr className="border-gray-200" />
                <div className="flex justify-between text-lg font-bold"><span>Totale netto</span><span className="font-mono">{totals.total_amount?.toFixed(2)?.replace('.',',')} €</span></div>
              </div>
            </div>

            {quote.terms_text && <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500"><p className="font-semibold mb-1">Condizioni:</p><p>{quote.terms_text}</p></div>}
            {totals.legal_notes?.length > 0 && (
              <div className="mt-4 text-xs text-gray-400">
                {totals.legal_notes.map((n, i) => <p key={i} className="mb-1">{n}</p>)}
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 border-gray-200">
          <button className="btn btn-secondary" onClick={onClose}>Chiudi</button>
          <button className="btn btn-primary" onClick={() => window.print()}>Stampa / PDF</button>
        </div>
      </div>
    </div>
  );
}

